import React, { useState, useEffect } from 'react';
import { getWorkflowHealth } from '../../utils/adminApi';
import { WORKFLOW_REGISTRY, computeWorkflowHealth, HEALTH_COLORS } from './workflowConfig';
import '../../styles/NotificationWorkflows.css';

// ─── Helpers ──────────────────────────────────────────────────────────────────
function getRelativeTime(dateStr) {
  if (!dateStr) return null;
  const diff = Date.now() - new Date(dateStr).getTime();
  const days = Math.floor(diff / (1000 * 60 * 60 * 24));
  if (days >= 30) return '30+ days';
  if (days >= 1) return `${days}d ago`;
  const hours = Math.floor(diff / (1000 * 60 * 60));
  if (hours >= 1) return `${hours}h ago`;
  const mins = Math.floor(diff / 60000);
  return mins < 1 ? 'just now' : `${mins}m ago`;
}

function formatPct(rate) {
  if (rate == null || isNaN(rate)) return '—';
  return `${Math.round(rate * 1000) / 10}%`;
}

function deliveryColor(rate) {
  if (rate == null) return '#888';
  return rate >= 0.95 ? '#4ade80' : '#fbbf24';
}

function engagementColor(rate) {
  if (rate == null) return '#888';
  if (rate > 0.5) return '#4ade80';
  if (rate >= 0.2) return '#fbbf24';
  return '#f87171';
}

function channelLabel(channel) {
  if (channel === 'email') return '📧 Email';
  if (channel === 'push') return '📱 Push';
  return '📧📱 Both';
}

// ─── Compute metrics for one workflow ────────────────────────────────────────
function computeWorkflowMetrics(wf, healthData) {
  const { emails, latestEmails, pushes, latestPush } = healthData;

  if (wf.channel === 'push') {
    const matching = pushes.filter(p => p.category === wf.matchValue);
    const latestMatch = latestPush.find(p => p.category === wf.matchValue);

    const volume7d = matching.length;
    const deliveredStatuses = new Set(['delivered', 'opened', 'clicked']);
    let deliveredCount = 0;
    let openedCount = 0;

    matching.forEach(p => {
      const logs = p.push_delivery_logs || [];
      const isDelivered = logs.some(l => deliveredStatuses.has(l.status));
      if (isDelivered) {
        deliveredCount++;
        openedCount++;
      }
    });

    const deliveryRate = volume7d > 0 ? deliveredCount / volume7d : null;
    const engagementRate = deliveredCount > 0 ? openedCount / deliveredCount : null;

    // lastFiredAt: most recent from matching, or from latestPush
    let lastFiredAt = null;
    if (matching.length > 0) {
      lastFiredAt = matching.reduce((latest, p) =>
        !latest || p.created_at > latest ? p.created_at : latest, null
      );
    } else if (latestMatch) {
      lastFiredAt = latestMatch.created_at;
    }

    return { volume7d, deliveryRate, engagementRate, lastFiredAt };
  } else {
    // email or both — use email data
    const matching = emails.filter(e =>
      e.template_name === wf.matchValue || e.template_key === wf.matchValue
    );
    const latestMatch = latestEmails.find(e =>
      e.template_name === wf.matchValue || e.template_key === wf.matchValue
    );

    const volume7d = matching.length;
    const deliveredStatuses = new Set(['delivered', 'opened', 'clicked']);
    let deliveredCount = 0;
    let openedCount = 0;

    matching.forEach(e => {
      if (deliveredStatuses.has(e.status)) deliveredCount++;
      if (e.status === 'opened' || e.status === 'clicked') openedCount++;
    });

    const deliveryRate = volume7d > 0 ? deliveredCount / volume7d : null;
    const engagementRate = deliveredCount > 0 ? openedCount / deliveredCount : null;

    let lastFiredAt = null;
    if (matching.length > 0) {
      lastFiredAt = matching.reduce((latest, e) =>
        !latest || e.sent_at > latest ? e.sent_at : latest, null
      );
    } else if (latestMatch) {
      lastFiredAt = latestMatch.sent_at;
    }

    return { volume7d, deliveryRate, engagementRate, lastFiredAt };
  }
}

// ─── Main Component ───────────────────────────────────────────────────────────
const WorkflowsTab = () => {
  const [healthData, setHealthData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [channelFilter, setChannelFilter] = useState('all');
  const [triggerFilter, setTriggerFilter] = useState('all');

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const data = await getWorkflowHealth();
        setHealthData(data);
      } catch (err) {
        console.error('Error loading workflow health:', err);
        setHealthData({ emails: [], latestEmails: [], pushes: [], latestPush: [] });
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  if (loading || !healthData) {
    return <div className="notif-workflows-loading">Loading workflows...</div>;
  }

  // Build enriched workflow rows
  const rows = WORKFLOW_REGISTRY.map(wf => {
    const metrics = computeWorkflowMetrics(wf, healthData);
    const health = computeWorkflowHealth({
      deliveryRate: metrics.deliveryRate,
      engagementRate: metrics.engagementRate,
      lastFiredAt: metrics.lastFiredAt,
      volume7d: metrics.volume7d,
    });
    return { ...wf, ...metrics, health };
  });

  // Filter
  const filtered = rows.filter(r => {
    if (channelFilter !== 'all' && r.channel !== channelFilter) return false;
    if (triggerFilter !== 'all' && r.trigger !== triggerFilter) return false;
    return true;
  });

  // Summary counts
  const counts = rows.reduce((acc, r) => {
    acc[r.health] = (acc[r.health] || 0) + 1;
    return acc;
  }, {});

  const summaryItems = [
    { key: 'healthy', label: 'Active' },
    { key: 'degraded', label: 'Degraded' },
    { key: 'stale', label: 'Stale' },
    { key: 'inactive', label: 'Inactive' },
  ];

  return (
    <div className="notif-workflows">
      {/* ── Summary bar ── */}
      <div className="notif-workflows-summary">
        <div className="notif-workflows-badges">
          {summaryItems.map(({ key, label }) => (
            <div key={key} className="notif-workflows-badge">
              <span className="notif-workflows-badge-dot" style={{ backgroundColor: HEALTH_COLORS[key] }} />
              <span className="notif-workflows-badge-count">{counts[key] || 0}</span>
              <span className="notif-workflows-badge-label">{label}</span>
            </div>
          ))}
        </div>
        <div className="notif-workflows-filters">
          <select
            className="notif-workflows-select"
            value={channelFilter}
            onChange={e => setChannelFilter(e.target.value)}
          >
            <option value="all">All Channels</option>
            <option value="email">📧 Email</option>
            <option value="push">📱 Push</option>
            <option value="both">📧📱 Both</option>
          </select>
          <select
            className="notif-workflows-select"
            value={triggerFilter}
            onChange={e => setTriggerFilter(e.target.value)}
          >
            <option value="all">All Triggers</option>
            <option value="auto">Auto</option>
            <option value="manual">Manual</option>
          </select>
        </div>
      </div>

      {/* ── Table ── */}
      <div className="notif-workflows-table">
        <div className="notif-workflows-table-header">
          <div />
          <div>Workflow</div>
          <div>Channel</div>
          <div>Trigger</div>
          <div>Last Fired</div>
          <div>Vol 7d</div>
          <div>Delivery</div>
          <div>Engagement</div>
          <div>Health</div>
        </div>

        {filtered.length === 0 ? (
          <div className="notif-workflows-empty">No workflows match the selected filters.</div>
        ) : (
          filtered.map(row => {
            const healthColor = HEALTH_COLORS[row.health] || '#888';
            const rowClass = [
              'notif-workflows-row',
              row.health === 'degraded' || row.health === 'stale' ? 'degraded' : '',
              row.health === 'inactive' ? 'inactive' : '',
            ].filter(Boolean).join(' ');

            return (
              <div key={row.id} className={rowClass}>
                {/* Health dot */}
                <div className="notif-workflows-dot-col">
                  <span
                    className="notif-workflows-health-dot"
                    style={{ backgroundColor: healthColor }}
                  />
                </div>

                {/* Name + edge function */}
                <div className="notif-workflows-name-col">
                  <div className="notif-workflows-name">{row.name}</div>
                  <div className="notif-workflows-edge-fn">{row.edgeFunction}</div>
                </div>

                {/* Channel */}
                <div className="notif-workflows-channel">{channelLabel(row.channel)}</div>

                {/* Trigger */}
                <div>
                  <span
                    className="notif-workflows-trigger-badge"
                    style={{
                      backgroundColor: row.trigger === 'auto' ? '#dcfce7' : '#e5e7eb',
                      color: row.trigger === 'auto' ? '#16a34a' : '#6b7280',
                    }}
                  >
                    {row.trigger === 'auto' ? 'Auto' : 'Manual'}
                  </span>
                </div>

                {/* Last Fired */}
                <div className="notif-workflows-last-fired">
                  {row.lastFiredAt ? getRelativeTime(row.lastFiredAt) : <span style={{ color: '#9ca3af' }}>Never</span>}
                </div>

                {/* Volume 7d */}
                <div className="notif-workflows-volume">{row.volume7d}</div>

                {/* Delivery % */}
                <div style={{ color: deliveryColor(row.deliveryRate), fontWeight: 600, fontSize: '0.8125rem' }}>
                  {formatPct(row.deliveryRate)}
                </div>

                {/* Engagement % */}
                <div style={{ color: engagementColor(row.engagementRate), fontWeight: 600, fontSize: '0.8125rem' }}>
                  {formatPct(row.engagementRate)}
                </div>

                {/* Health badge */}
                <div>
                  <span
                    className="notif-workflows-health-badge"
                    style={{ backgroundColor: healthColor + '22', color: healthColor, borderColor: healthColor + '55' }}
                  >
                    {row.health.charAt(0).toUpperCase() + row.health.slice(1)}
                  </span>
                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
};

export default WorkflowsTab;
